import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import GeneralLayout from "./components/layouts/GeneralLayout";
import NavigationProvider from "./components/NavigationProvider";
import NotificationPage from "./components/NotificationPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { TeamsAuthPopup } from "./components/TeamsAuthPopup";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DashboardProvider, useDashboardData } from "./contexts/DashboardContext";
import { TeamAppProvider } from "./contexts/TeamAppContext";
import { msalConfig } from "./lib/authConfig";
import Unauthorized from "./pages/Unauthorized";
import NotFound from "./pages/NotFound";
import { ROUTES } from "./lib/constants/routes";
import { isTeamsAuthPopup } from "./services/teamsAuthPopup";

// Import CSS for react-grid-layout
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { AppDataProvider, WelcomeMessage } from "./contexts/AppDataContext";
import React from "react";
import { VersionProvider } from "./contexts/VersionContext";
import { VersionNotification } from "./components/VersionNotification";

const msalInstance = new PublicClientApplication(msalConfig);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const DashboardLayoutSelector: React.FC = () => {
  const { id } = useParams();
  const { current, setCurrent } = useDashboardData();

  React.useEffect(() => {
    if (id && current?.id !== Number(id)) {
      setCurrent(Number(id));
    }
  }, [id]);

  if (!current) return null;
  return <GeneralLayout />;
};

// Root route handler component to manage authentication flow
const RootRoute = () => {
  const location = useLocation();
  const { login } = useAuth();
  // Check if this is an MSAL redirect (look for authentication parameters in URL)
  const isAuthRedirect =
    location.hash.includes("id_token=") ||
    location.hash.includes("access_token=") ||
    location.hash.includes("code=") ||
    location.search.includes("error=") ||
    location.search.includes("state=");

  // If we're processing a redirect, show a loading indicator
  if (isAuthRedirect) {
    login().catch((e) => console.error("Login during redirect failed:", e));
    return <NotificationPage content={WelcomeMessage} enableRotation={true} />;
  }
  else
    // Otherwise, redirect to the dashboard
    return <Navigate to="/dashboard/general" replace />;
};

const AppContent = () => {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route
        path="/dashboard/general"
        element={
          <ProtectedRoute>
            <DashboardProvider>
              <GeneralLayout />
            </DashboardProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/:id"
        element={
          <ProtectedRoute>
            <DashboardProvider>
              <DashboardLayoutSelector />
            </DashboardProvider>
          </ProtectedRoute>
        }
      />
      <Route path={ROUTES.AUTH.UNAUTHORIZED} element={<Unauthorized />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  // The Teams sign-in popup only signs in and hands the token back: it must not mount the app (and its auth flow).
  if (isTeamsAuthPopup()) {
    return <TeamsAuthPopup />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TeamAppProvider>
        <MsalProvider instance={msalInstance}>
          <BrowserRouter>
            <AuthProvider>
              <AppDataProvider>
                <VersionProvider>
                  <TooltipProvider>
                    <NavigationProvider>
                      <AppContent />
                    </NavigationProvider>
                    {/* Temporarily hidden until the version notification is re-enabled */}
                    {/* <VersionNotification /> */}
                    <Toaster />
                    <Sonner />
                  </TooltipProvider>
                </VersionProvider>
              </AppDataProvider>
            </AuthProvider>
          </BrowserRouter>
        </MsalProvider>
      </TeamAppProvider>
    </QueryClientProvider>
  );
};

export default App;
